import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle, Warning, Eye, EyeSlash, Lock } from '@phosphor-icons/react';
import BrandLogo from '../components/BrandLogo';

export default function SetupAccountPage() {
  const { token } = useParams();
  const { API, setUser, setCurrentOrg } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [info, setInfo] = useState(null);
  const [error, setError] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    axios.get(`${API}/auth/setup-account/${token}`)
      .then(r => { setInfo(r.data); if (r.data.name) setName(r.data.name); })
      .catch(err => setError(err.response?.data?.detail || 'Invalid setup link'))
      .finally(() => setLoading(false));
  }, [token, API]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirmPassword) { setError('Passwords do not match'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    if (!info?.existing_user && !name.trim()) { setError('Name is required'); return; }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/auth/setup-account/${token}`,
        { password, name: name.trim() }, { withCredentials: true });
      setUser(data);
      if (data.memberships?.length > 0) {
        const first = data.memberships[0];
        setCurrentOrg(first);
        document.cookie = `current_org_id=${first.org_id};path=/;max-age=31536000`;
      }
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to activate account');
    } finally { setSubmitting(false); }
  };

  if (loading) return <CenteredShell><div className="text-zinc-400 text-sm">Verifying your invitation…</div></CenteredShell>;

  if (error && !info) return (
    <CenteredShell>
      <Card>
        <CardContent className="pt-8 pb-6 text-center">
          <Warning size={40} weight="fill" className="text-red-500 mx-auto mb-3" />
          <h1 className="font-['Outfit'] text-xl font-bold text-zinc-900 mb-2">Invitation link invalid</h1>
          <p className="text-sm text-zinc-500">{error}</p>
          <Button className="mt-5" onClick={() => navigate('/login')}>Go to Login</Button>
        </CardContent>
      </Card>
    </CenteredShell>
  );

  return (
    <CenteredShell>
      <Card className="w-full max-w-md">
        <CardContent className="pt-8 pb-6">
          <div className="mb-6 text-center">
            <div className="mx-auto mb-3 flex justify-center">
              <BrandLogo size="xl" />
            </div>
            <h1 className="font-['Outfit'] text-2xl font-bold text-zinc-900">Welcome to Inspiro Projects</h1>
            <p className="text-sm text-zinc-500 mt-1">
              You've been invited to <strong className="text-zinc-800">{info.org_name}</strong> as <strong className="text-zinc-800">{info.role}</strong>
            </p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="setup-account-form">
            <div>
              <Label className="text-xs font-semibold text-zinc-600">Email</Label>
              <Input value={info.email} disabled className="mt-1 bg-zinc-50 text-zinc-500" data-testid="setup-email-input" />
            </div>
            {!info.existing_user && (
              <div>
                <Label className="text-xs font-semibold text-zinc-600">Your Name</Label>
                <Input value={name} onChange={e => setName(e.target.value)} placeholder="Jane Doe" className="mt-1" required data-testid="setup-name-input" />
              </div>
            )}
            <div>
              <Label className="text-xs font-semibold text-zinc-600">{info.existing_user ? 'New Password' : 'Set Password'}</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" size={16} />
                <Input type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="Minimum 6 characters" className="pl-9 pr-9" required data-testid="setup-password-input" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600" tabIndex={-1} data-testid="toggle-setup-password">
                  {showPassword ? <EyeSlash size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold text-zinc-600">Confirm Password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" size={16} />
                <Input type={showPassword ? 'text' : 'password'} value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Re-enter password" className="pl-9" required data-testid="setup-confirm-input" />
              </div>
            </div>
            {error && <div className="text-xs text-red-600 bg-red-50 border border-red-100 rounded p-2">{error}</div>}
            <Button type="submit" disabled={submitting} className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="setup-submit-button">
              {submitting ? 'Activating…' : 'Activate Account'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </CenteredShell>
  );
}

function CenteredShell({ children }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 via-white to-purple-50 p-4">
      <div className="w-full max-w-md">{children}</div>
    </div>
  );
}
